namespace Solovis.Reports.Parameters {

  interface Manager {
    name: string;
    managerId: number;
    resourceId: number;
  }

  export class ManagerParameterControl extends Solovis.UI.BaseControl {
    public initialParameterValue;
    public currentManager;

    public selectedManagerName;
    public selectedManagerLink;

    private _selectionDialog: Solovis.UI.Dialog;

    private _portalServices: Solovis.Portal.IPortalServices;

    // NOTE: related to new render
    private _managers: Array<Manager>;
    static hasNewRender = true;
    private _selectRef;

    constructor(options) {
      super(options);

      this._portalServices = options.portalServices;

      this.initialParameterValue = (typeof options.initialValue != "undefined") ? options.initialValue : null;
      this.currentManager = this.initialParameterValue;
    }

    public static preload(newValue, callback, portalServices: Solovis.Portal.IPortalServices) {
      if (!portalServices) {
        portalServices = new Solovis.Portal.SitePortalServices();
      }

      if (newValue && (newValue.managerId || newValue.resourceId || newValue.managerCode)) {
        var loadOptions: any = { manager: {} };
        if (newValue.managerId)
          loadOptions.manager.id = newValue.managerId;
        if (newValue.resourceId)
          loadOptions.manager.resourceId = newValue.resourceId;
        if (newValue.managerCode)
          loadOptions.manager.code = newValue.managerCode;

        portalServices.reportDataLoader().loadDataSetAsync("/Solovis/Metadata/ParameterLoad",
          loadOptions,
          function (response, success) {
            if (success) {
              var dataSet = response.result.dataSet;
              var managerData = dataSet.manager ? dataSet.manager : null;
              callback(managerData, true);
            }
            else {
              callback(null, false);
            }
          });
      }
      else {
        callback(null, true);
      }
    };

    public static preloadMultiple(
      values: any[],
      callback: (items: any[], success: boolean) => void,
      portalServices: Solovis.Portal.IPortalServices) {

      if (!portalServices) {
        portalServices = new Solovis.Portal.SitePortalServices();
      }

      if (!values || values.length === 0) {
        callback([], true);
        return;
      }

      var loadedManagers: any[] = [];
      var pendingLoads = values.length;
      var hasError = false;

      values.forEach(function(value) {
        ManagerParameterControl.preload(value, function(loadedManager, success) {
          pendingLoads--;
          
          if (success && loadedManager) {
            loadedManagers.push(loadedManager);
          } else if (!success) {
            hasError = true;
          }
          
          if (pendingLoads === 0) {
            callback(loadedManagers, !hasError);
          }
        }, portalServices);
      });
    }

    public static shouldPreload(value) {
      if (!value || (value.managerId && value.resourceId && value.name)) {
        // Already loaded.
        return false;
      }

      return true;
    }

    public static serializeParameterValue(value): any {
      if (value) {
        if (value.managerId) {
          return { managerId: value.managerId };
        }
        else if (value.resourceId) {
          return { resourceId: value.resourceId };
        }
        else if (value.managerCode) {
          return { managerCode: value.managerCode };
        }
      }

      return null;
    }

    public static getLabel(value: Manager) {
      return value?.name;
    }

    public doRender() {
      if (this.options.renderMode === 'modern') {
        this.doRenderNew();
      } else {
        this.doRenderClassic();
      }
    }

    private async doRenderNew() {
      var self = this;
      const target = this.container[0];

      const { fetchManagers } = await (window as any).importShim('@solovis/pa-utils');
      await Solovis.UX.Utils.placeComponent(
        "AsyncSelect",
        target,
        {
          cacheOptions: true,
          defaultValue: (self.options as any).initialValue || undefined,
          getOptionLabel: ManagerParameterControl.getLabel,
          getOptionValue: (option: Manager) => option.resourceId + '',
          loadOn: "input",
          loadOptions: (search: string) => new Promise(async (resolve, reject) => {
            try {
              const options = await fetchManagers({ filterText: search, maxItems: 10 });
              if (options.hasMoreData) {
                options.data.push({
                  label: "More Results",
                  managerId: -1,
                  resourceId: -1,
                  originalQuery: { filterText: search },
                })
              }
              resolve(options.data);
            } catch (e) {
              reject(e);
            }
          }),
          onChange: (selectedItem) => {
            //if nothing selected don't notify
            if (!selectedItem) {
              return;
            }
            if (selectedItem.label === "More Results") {
              fetchManagers({ filterText: selectedItem.originalQuery.filterText, includeAll: true }).then(managers => {
                var data = managers.data;

                Solovis.UI.Dialogs.StringFilterListDialog.showDialog({
                  title: "Select Manager",
                  selectCallback: function (selectedItem) {
                    self._selectRef.setValue(selectedItem);
                  },
                  stringFilterListControlOptions: {
                    listItems: data,
                    itemDivGenerator: function (itemHolder, filterOptions) {
                      var itemDiv = $("<div>").text(itemHolder.item.label);
                      //itemDiv.prepend(Solovis.UI.Icons.createEntityIcon({ "padding-right": "10px" })); TODO: add icon if/when available
                      return itemDiv;
                    }
                  }
                });
              });
              setTimeout(() => { //wait then clear the more results
                self._selectRef.clearValue();
              }, (0));
              return;
            }
            self.currentManager = selectedItem;
            self.notifyParameterValueChanged();
          },
          ref: (ref) => {
            self._selectRef = ref;
          }
        }
      );
    }

    private doRenderClassic() {
      var self = this;

      this.selectedManagerName = $("<a>").appendTo(this.container);
      Solovis.UI.PopupLinkPanel.onElementClick(this.selectedManagerName, function (e) {
        if (self.currentManager && self.currentManager.managerId) {
          // NOTE: Do not use autoClickSingleItem here. It's not a good experience if someone is trying
          //   to click the edit link and ends up jumping to another page instead.
          Solovis.UI.PopupLinkPanel.showLinks({
            offset: { left: e.pageX, top: e.pageY },
            links: self._portalServices.reportLinker().getManagerLinks(self.currentManager.managerId)
          });
        }
      });

      this.selectedManagerLink = $("<a>").html("<span class='fa fa-pencil'></span>").appendTo(this.container);

      Solovis.UI.DomHelper.focusableClick(this.selectedManagerLink, function () {
        self._toggleSelectorDialog(function (manager) {
          self._setCurrentManagerData(manager);
          self.notifyParameterValueChanged();
        });
      });

      // Set initial parameter value text.
      if (this.currentManager) {
        this._setCurrentManagerData(this.currentManager);
      }

      // Setup drop handler for Solovis reference.
      Solovis.Site.DragDropManager.setupSolovisRefDrop(this.container, function (solovisRef) {
        if (!solovisRef.type || solovisRef.type == "manager") {
          self.setParameterValue(solovisRef.value);
        }
      });
    };

    public _setCurrentManagerData(managerData) {
      this.currentManager = managerData;
      if (this.currentManager) {
        this.selectedManagerName.text(ManagerParameterControl.getLabel(this.currentManager) + " ");
      }
      else {
        this.selectedManagerName.text("");
      }
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, ManagerParameterControl, this._setCurrentManagerData, newValue, more);
    }

    public getParameterValue() {
      return Solovis.deepCopy(this.currentManager);
    }

    public notifyParameterValueChanged() {
      $(this).trigger("parameterValueChanged");
    }

    public focus() {
      $(this.selectedManagerLink).focus();
    }

    public _toggleSelectorDialog(selectionCallback) {
      var self = this;

      if (this._selectionDialog) {
        // We already have the selection dialog open, so close it.
        this._selectionDialog.close();
        this._selectionDialog = null;
        return;
      }

      var managerSelectorControlHostDiv = $("<div>");
      var managerSelectorControlHost = new Solovis.UI.ControlHost({ container: managerSelectorControlHostDiv });
      var managerSelectorControl = new Solovis.InvDataSelectors.ManagerSelectorControl({
        controlHost: managerSelectorControlHost,
        portalServices: this._portalServices,
        selectionCallback: function (manager) {
          selectionCallback(manager);
          self._selectionDialog.close();
        },
        cancelCallback: function () {
          self._selectionDialog.close();
        }
      });
      managerSelectorControlHost.setCurrentControl(managerSelectorControl);

      // Determine dialog position.        
      var linkOffset = $(this.selectedManagerName).offset();
      var linkHeight = $(this.selectedManagerLink).outerHeight();
      var dialogPosition = {
        left: linkOffset.left,
        top: linkOffset.top + linkHeight + 5
      };

      var adjustedPosition = Solovis.UI.Dialog.autoAdjustSizeAndPosToViewport(dialogPosition, { width: 350, height: 150 });

      this._selectionDialog = new Solovis.UI.Dialog({
        container: managerSelectorControlHostDiv,
        ownerDialog: Solovis.UI.Dialog.findContainingDialog(this.container),

        title: false,
        autoOpen: true,
        destroyOnClose: true,
        actions: ["Close"],
        noAnimation: true,
        position: adjustedPosition,

        buttons: [
          {
            label: "Clear",
            click: function () {
              selectionCallback(null);
              self._selectionDialog.close();
            }
          },
          {
            label: "Cancel",
            click: function () {
              self._selectionDialog.close();
            }
          }
        ],

        activate: function () {
          managerSelectorControl.focus();
        },

        deactivate: function () {
          self._selectionDialog = null;
        }
      });
    };
  }
}